import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AvailabilityCalendar from "./AvailabilityCalendar";
import QuoteRequestForm from "./QuoteRequestForm";
import { fetchAvailability, type DateRange, type Rig } from "../../lib/bookingApi";
import { useWheelbaseConfig } from "../../lib/wheelbase";

interface Props {
  rig: Rig;
  heading: string;
  prebook?: boolean;
  availableFrom?: string;
}

const BookingCard = ({ rig, heading, prebook, availableFrom }: Props) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [ranges, setRanges] = useState<DateRange[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const config = useWheelbaseConfig();
  const rentalId = config?.rentals[rig];
  const wheelbaseReady = Boolean(config);

  useEffect(() => {
    if (rentalId && !prebook) {
      return;
    }
    let cancelled = false;
    fetchAvailability(rig)
      .then((next) => {
        if (!cancelled) {
          setRanges(next);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRanges([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rig, rentalId, prebook]);

  function open() {
    dialogRef.current?.showModal();
  }

  function close() {
    dialogRef.current?.close();
  }

  if (!prebook && !wheelbaseReady) {
    return (
      <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm">
        <h3 className="font-semibold text-2xl mb-2">{heading}</h3>
        <p className="text-zinc-500 text-sm">Loading…</p>
      </div>
    );
  }

  if (rentalId && !prebook) {
    return (
      <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm">
        <h3 className="font-semibold text-2xl mb-2">{heading}</h3>
        <p className="text-zinc-500 text-sm mb-6">
          Check dates and book online.
        </p>
        <Link
          to={`/fleet/${rig}`}
          className="block w-full bg-mars hover:bg-sunset text-white text-center py-6 rounded-2xl font-semibold text-lg transition-all"
        >
          Book now
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm">
        <h3 className="font-semibold text-2xl mb-2">{heading}</h3>
        <p className="text-zinc-500 text-sm mb-6">
          {prebook
            ? "Serenity arrives November 2026. Tell us the trip you want — this is interest, not a paid reservation."
            : "Check available dates and tell us about the trip. We’ll email a quote"}
        </p>
        <button
          type="button"
          onClick={open}
          className="block w-full bg-mars hover:bg-sunset text-white text-center py-6 rounded-2xl font-semibold text-lg transition-all"
        >
          {prebook ? "Pre-book Serenity" : "Request a quote"}
        </button>
      </div>

      <dialog
        ref={dialogRef}
        className="w-[min(72rem,calc(100vw-1.5rem))] max-h-[90vh] overflow-y-auto rounded-3xl p-0 bg-white shadow-2xl backdrop:bg-alt-blk/60"
        onClick={(event) => {
          if (event.target === dialogRef.current) {
            close();
          }
        }}
      >
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <h2 className="font-semibold text-2xl">
                {prebook ? "Pre-book Serenity" : "Request a quote"}
              </h2>
              <p className="text-zinc-500 text-sm mt-1">
                {prebook
                  ? "Pick dates from November 2026 onward. We’ll follow up when she is ready to rent."
                  : "Pick pickup and return dates, then send a few details."}
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              className="text-3xl leading-none text-zinc-500 hover:text-alt-blk px-2"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="lg:grid lg:grid-cols-2 lg:gap-10 lg:items-start">
            <AvailabilityCalendar
              ranges={ranges}
              startDate={startDate}
              endDate={endDate}
              loading={loading}
              minDate={availableFrom}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
            <div className="mt-8 lg:mt-0">
              <QuoteRequestForm
                rig={rig}
                startDate={startDate}
                endDate={endDate}
                prebook={prebook}
              />
            </div>
          </div>
        </div>
      </dialog>
    </>
  );
};

export default BookingCard;
